import { head } from "lodash"
import moment from "moment"
import React from "react"
import { Col, Dropdown, Row } from "react-bootstrap"
import { ChevronDown, ChevronUp } from "react-bootstrap-icons"
import BootstrapTable, { ColumnDescription } from "react-bootstrap-table-next"
import filterFactory from "react-bootstrap-table2-filter"
import paginationFactory from "react-bootstrap-table2-paginator"
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit"
import { useTranslation } from "react-i18next"
import { useAppDispatch } from "../../app/hooks"
import { SearchBar, SelectFilterColums } from "../../components/Tables/Columns"
import CustomTotal from "../../components/Tables/CustomTotal"
import pageListRenderer from "../../components/Tables/PageListRenderer"
import sizePerPageOptionRenderer from "../../components/Tables/SizePerPageOptionRenderer"
import { setConsolBox } from "../../store/consolidation.store"

let suiteFilter: any

function ConsolidationsTable({
    consolidations,
    setShow
}: {
    consolidations: Record<string, any> | Record<string, any>[]
    setShow: Function
}) {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const onFilter = (value: string | number, id: string, subId: string) => {
        if (!value) value = ""
        if (id === "suite") suiteFilter(value)
    }
    function parcelCreatedDate(cell: string) {
        if (cell) return moment(cell).format("DD MMM yyyy")
    }

    const getAllConsolidations = () => {
        const oneConsolidationSuites = [] as any[]
        let multiConsolidationSuites = [] as any[]

        for (let i = 0; i < Object.values(consolidations).length; i++) {
            if (Object.values(consolidations)[i]?.length > 1) {
                multiConsolidationSuites.push(Object.values(consolidations)[i])
            } else {
                oneConsolidationSuites.push({
                    ...head(Object.values(consolidations)[i]),
                    ParcelSuite: [head(Object.values(consolidations)[i] as any[]).ParcelSuite],
                    parcels: [head(Object.values(consolidations)[i] as any[]).parcels]
                })
            }
        }

        multiConsolidationSuites = multiConsolidationSuites?.map((consolGroup: Record<string, any>[]) => ({
            id: consolGroup[0]?.id,
            suite: consolGroup[0]?.suite,
            ParcelSuite: consolGroup.map((consol) => consol.ParcelSuite),
            parcels: consolGroup.map((consol) => consol.parcels)
        }))

        const allConsolidations = oneConsolidationSuites.concat(multiConsolidationSuites) || []

        return allConsolidations
    }

    const consolidationColumns = [
        {
            dataField: "id",
            text: Array.isArray(consolidations) ? t("Box ID") : t("Suite ID"),
            classes: "highlighted-text"
        },
        {
            dataField: "suite",
            text: Array.isArray(consolidations) ? t("Box UUID") : t("Suite No."),
            classes: "highlighted-text",
            formatter: (cell: Record<string, any>[], row: Record<string, any>) => {
                return Array.isArray(consolidations) ? row?.uuid : cell
            }
        },
        {
            dataField: "ParcelHasStatuses",
            text: t("Status"),
            hidden: !Array.isArray(consolidations),
            formatter: (cell: Record<string, any>[], row: Record<string, any>) => {
                return cell?.length > 0 ? cell[0]?.reason : "-"
            }
        },
        {
            text: t("Customer"),
            dataField: "user_pin",
            formatter: (cell: string | number, row: Record<string, any>) => {
                return cell ? (
                    <div>
                        <span className="d-flex">{cell}</span>
                        <span>{row?.name}</span>
                    </div>
                ) : (
                    ""
                )
            },
            csvFormatter: (cell: string | number, row: Record<string, any>) => {
                return cell ? cell + " ," + row?.name : ""
            }
        },
        {
            text: t("Consolidations"),
            dataField: "parcels",
            formatter: (cell: Record<string, any>[], row: Record<string, any>) => {
                return Array.isArray(consolidations) ? row?.secondary_boxes?.length + 1 : cell?.length
            }
        }
    ]

    const expandRow = {
        renderer: (row: Record<string, any>) => {
            const columns = [
                {
                    dataField: "uuid",
                    text: t("Consolidation"),
                    classes: "highlighted-text"
                },
                {
                    dataField: "created_at",
                    text: t("Submitted on"),
                    classes: "highlighted-text",
                    formatter: (cell: string, row: Record<string, any>) => {
                        return parcelCreatedDate(cell)
                    }
                },
                {
                    dataField: "Children",
                    text: t("No. Of Parcels"),
                    classes: "highlighted-text",
                    hidden: Array.isArray(consolidations),
                    formatter: (cell: Record<string, any>[], row: Record<string, any>) => {
                        return cell?.length || 0
                    }
                },
                {
                    dataField: "Children",
                    text: t("Action"),
                    classes: "highlighted-text",
                    formatter: (cell: Record<string, any>[], row: Record<string, any>) => {
                        return row?.uuid?.includes("-") ? null : (
                            <Dropdown className="actions-menu">
                                <Dropdown.Toggle
                                    variant="primary"
                                    id="dropdown-basic"
                                    size={"sm"}
                                    className="py-0">
                                    {t("Actions")}
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        onClick={async () => {
                                            await dispatch(
                                                setConsolBox({
                                                    uuid: row?.uuid,
                                                    Children: Array.isArray(consolidations)
                                                        ? [row, ...(row?.secondary_boxes || [])]
                                                        : cell,
                                                    shippingService: Number(row.shipping_company_id),
                                                    suite: Array.isArray(consolidations)
                                                        ? undefined
                                                        : row?.suite
                                                })
                                            )
                                            setShow(
                                                Array.isArray(consolidations)
                                                    ? "addWeightAndDims"
                                                    : "consolidate"
                                            )
                                        }}>
                                        {t("View")}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={async () => {
                                            await dispatch(setConsolBox({ ...row }))
                                            setShow("cancelConfirmModal")
                                        }}>
                                        {t("Cancel Consolidation")}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        )
                    }
                }
            ]
            return (
                <>
                    <BootstrapTable
                        keyField="uuid"
                        data={
                            Array.isArray(consolidations)
                                ? [row, ...(row?.secondary_boxes || [])]
                                : row?.parcels?.map((parcel: Record<string, any>) => ({
                                      ...parcel,
                                      suite: row?.ParcelSuite[0].suite_id
                                  }))
                        }
                        columns={columns}
                        wrapperClasses="inner-table my-3 mx-4 "
                        headerClasses="header-class"
                        bordered={false}
                        noDataIndication={t("No_Record")}
                        rowClasses="inner-table-row"
                        condensed={true}
                    />
                </>
            )
        },
        showExpandColumn: true,
        expandByColumnOnly: true,
        expandHeaderColumnRenderer: ({ isAnyExpands }: { isAnyExpands: boolean }) => {
            return <></>
        },
        expandColumnRenderer: ({ expanded }: { expanded: boolean }) => {
            if (expanded) {
                return <ChevronUp color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
            }
            return <ChevronDown color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
        }
    }

    const options = {
        sizePerPage: 50,
        hideSizePerPage: false,
        hidePageListOnlyOnePage: true,
        sizePerPageOptionRenderer,
        showTotal: true,
        pageListRenderer,
        paginationTotalRenderer: CustomTotal
    }

    const { ExportCSVButton } = CSVExport

    return (
        <div className="table-responsive px-4">
            {consolidationColumns && (
                <ToolkitProvider
                    keyField={Array.isArray(consolidations) ? "uuid" : "suite"}
                    data={Array.isArray(consolidations) ? consolidations : getAllConsolidations()}
                    columns={consolidationColumns as ColumnDescription<any, any>[]}
                    search={{ searchFormatted: true }}
                    //exportCSV={{
                    //    fileName: "ConsolidationsTable.csv",
                    //    noAutoBOM: false,
                    //    exportAll: false,
                    //    onlyExportFiltered: true
                    //}}
                >
                    {(props) => (
                        <div className="table-container my-4">
                            <Row className="search mx-2">
                                <Col xs={12} sm={12} md={12} lg={8} className="d-flex px-3 float-end">
                                    <SelectFilterColums
                                        preFilteredRows={props.baseProps.data}
                                        id="suite"
                                        text={t("Suite_Id")}
                                        {...props.searchProps}
                                        // onFilterClick={onFilter}
                                    />
                                </Col>
                                <Col xs={12} sm={12} md={12} lg={4} className="d-flex justify-content-end">
                                    <SearchBar {...props.searchProps} />
                                </Col>
                                {/*<Col className="d-none">
                                    <ExportCSVButton {...props.csvProps}>
                                        Export
                                    </ExportCSVButton>
                                </Col>*/}
                            </Row>
                            <Row className="table-data my-3">
                                <Col>
                                    <BootstrapTable
                                        expandRow={expandRow}
                                        {...props.baseProps}
                                        wrapperClasses="table-wrapper my-3 mx-4 "
                                        headerClasses="header-class"
                                        bordered={false}
                                        filter={filterFactory()}
                                        noDataIndication={t("No_Record")}
                                        rowClasses="custom-table-row"
                                        condensed={true}
                                        pagination={paginationFactory(options)}
                                        sort={{
                                            dataField: "created_at",
                                            order: "desc"
                                        }}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </ToolkitProvider>
            )}
        </div>
    )
}

export default ConsolidationsTable
